import cx from 'classnames';
import Link from 'next/link';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import React, { FunctionComponent } from 'react';

import useLinkProps from '../../../hooks/use-link-props';
import { addresses, phones } from '../../../shared';
import Breadcrumbs from '../../modules/breadcrumbs';
import CountriesContacts from '../../modules/countries-contacts';
import PhoneNotice from '../../modules/help-contacts/phone-notice';
import { IntercomWrapperGeneric } from '../../modules/intercom';
import IconEnvelope from '../../ui/icons/envelope';
import FeaturedLink from '../../ui/links/featured';

import ContactsSchema from './seo';

const contacts = [
  {
    flag: {
      src: `${process.env.PUBLIC_PREFIX}/svg/icons/flags/pt.svg`,
      alt: 'countries-contacts:countries.pt.name',
    },
    title: 'countries-contacts:countries.pt.name',
    attributes: [
      {
        label: 'countries-contacts:countries.phone',
        value: phones.pt,
        variant: 'phone',
      },
      {
        label: 'countries-contacts:countries.address',
        value: addresses.pt,
        variant: 'address',
      },
    ],
  },
  {
    flag: {
      src: `${process.env.PUBLIC_PREFIX}/svg/icons/flags/es.svg`,
      alt: 'countries-contacts:countries.es.name',
    },
    title: 'countries-contacts:countries.es.name',
    attributes: [
      {
        label: 'countries-contacts:countries.phone',
        value: phones.es,
        variant: 'phone',
      },
      {
        label: 'countries-contacts:countries.address',
        value: addresses.es,
        variant: 'address',
      },
    ],
  },
  {
    flag: {
      src: `${process.env.PUBLIC_PREFIX}/svg/icons/flags/fr.svg`,
      alt: 'countries-contacts:countries.fr.name',
    },
    title: 'countries-contacts:countries.fr.name',
    attributes: [
      {
        label: 'countries-contacts:countries.phone',
        value: phones.fr,
        variant: 'phone',
      },
      {
        label: 'countries-contacts:countries.address',
        value: addresses.fr,
        variant: 'address',
      },
    ],
  },
  {
    flag: {
      src: `${process.env.PUBLIC_PREFIX}/svg/icons/flags/en.svg`,
      alt: 'countries-contacts:countries.uk.name',
    },
    title: 'countries-contacts:countries.uk.name',
    attributes: [
      {
        label: 'countries-contacts:countries.phone',
        value: phones.uk,
        variant: 'phone',
      },
      {
        label: 'countries-contacts:countries.address',
        value: addresses.uk,
        variant: 'address',
      },
    ],
  },
];

const ContactsPage: FunctionComponent<TPageContacts> = ({ }) => {
  const { t } = useTranslation();
  const getLink = useLinkProps();

  return (
    <main>
      <ContactsSchema breadcrumbs={[]} />
      <Breadcrumbs items={[]} currentPage={t('common:contacts')} />
      <div className={cx('container mx-auto', 'pt-7 lg:pt-16 pb-14 px-4')}>
        <div className={cx('lg:grid grid-cols-[3fr_2fr]', 'w-full')}>
          <div className="">
            <h1
              className={cx(
                'font-bold',
                'text-2xl',
                'leading-6',
                'lg:leading-8'
              )}
            >
              {t('contacts:schedule.faq_title')}{' '}
            </h1>
            <Link {...getLink('faqs')} passHref legacyBehavior>
              <FeaturedLink>{t('contacts:schedule.faq_link')}</FeaturedLink>
            </Link>
            <div className={cx('mt-10', 'max-w-xl')}>
              <h2 className="font-bold text-xl mb-3">
                {t('contacts:schedule.title')}
              </h2>
              <Trans
                i18nKey="contacts:schedule.text"
                components={{ b: <b />, p: <p className="mb-6" /> }}
                values={{ timezone: `(GMT+${false ? '1' : '0'})` }}
              />
              <div className={cx('max-w-sm')}>
                <IntercomWrapperGeneric
                  variant="PRIMARY"
                  isCustomLauncher
                  data-intercom="contact-form"
                  initiateImmediately
                >
                  <span className="flex gap-4 justify-center">
                    <IconEnvelope /> {t('contacts:schedule.get-in-touch')}
                  </span>
                </IntercomWrapperGeneric>
              </div>
            </div>
          </div>
          <div
            className={cx(
              'separator-top',
              'lg:separator-left',
              'mt-6 lg:mt-0',
              'lg:px-12 pt-4 lg:pt-0'
            )}
          >
            <CountriesContacts
              contacts={contacts}
              classname="lg:grid grid-cols-2 gap-x-20 gap-y-4 [&_address]:tracking-[1px] w-fit"
            />
            <div className="mt-6">
              <PhoneNotice />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ContactsPage;
